import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { NavigationEnd, NavigationExtras, Params, PRIMARY_OUTLET, RedirectCommand, Router, UrlTree } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ORDER_CARD_BACK_STATE_KEY, ORDERS_CARD_BACK_STATE_KEY, orderCardRoute } from '@callcenter-app/pages/orders-archive/orders-archive.format';

/**
 * Where the operator is taken back after the OLD order editor (`/orders/edit`, the shared library's `HxOrderEditComponent`): the list
 * «Заказы» with its view, filters and tab, or the full card `/order/:id`. A PLACE, not a ready address: when the operator leaves for a new
 * order there is no id yet, and an edited order may turn into another one inside the editor (a transfer to another store) — the id is
 * taken from the route the editor finishes on.
 */
export type OldEditorReturnPlace =
  | { kind: 'list'; params: Params }
  /** `backState`: the card's own way back (to the «Заказы» list / the «Архив заказов» it was opened from) — the card that comes back gets it again. */
  | { kind: 'card'; backState?: Record<string, unknown> };

/**
 * What a page of the NEW editor adds when it sends the operator on: it is neither the list nor a card, so where she is taken back is not read off
 * its address (`place`), and the editor's «Отмена» leads to a page of its choice (`backLink`) instead of the page's own address.
 */
export interface OldEditorOpenOptions {
  place?: OldEditorReturnPlace;
  backLink?: string;
  /**
   * Called ONCE, when the editor has SAVED the order and the operator is taken to the list (the guard's redirect, {@link OldEditorReturnService.redirectFor}) — for the page that sent her to clear what it kept for her
   * (its drafts). Never called on the editor's «Отмена», when the term of the booking ends, or when she leaves by the menu or «Назад»: nothing was saved there, and what the page kept is still hers.
   */
  saved?: () => void;
}

/** What the old editor takes in its query (the library's camelCase contract): the order to edit, or the phone of the client of a new order. */
export interface OldEditorQuery {
  orderId?: number;
  phone?: string;
}

export const OLD_EDITOR_PATH = '/orders/edit';

/** The navigation state the full card keeps its «Заказы» / «Архив заказов» crumb in (`Location.getState()`). */
const CARD_BACK_STATE_KEYS: readonly string[] = [ORDERS_CARD_BACK_STATE_KEY, ORDER_CARD_BACK_STATE_KEY];

/** A return nobody used for this long is forgotten — the operator is no longer in that editor. */
export const OLD_EDITOR_RETURN_TTL_MS = 2 * 60 * 60 * 1000;

function isEditorUrl(url: string): boolean {
  return url.split(/[?#]/, 1)[0] === OLD_EDITOR_PATH;
}

/**
 * The shared editor (`ui/libraries/hx-order`, not ours) ends in the OLD view `/orders/{id}` — after «Сохранить» and when the order is
 * paid under it — whoever opened it: the `backLink` it is given is used for «Отмена» only. This service lets the new pages put the
 * operator back where she came from instead: a page leaves for the editor through {@link openEditor}, which remembers the place (kept in
 * memory, per tab); the guard of `/orders/:id` ({@link redirectFor}) turns the editor's exit into the list with the saved or the
 * just-created order open, or into its full card.
 *
 * It is armed only while the operator is in the editor she was sent to: any other way out (the menu, «Назад», «Отмена» with its
 * `backLink`) disarms it, and so does the lapse of {@link OLD_EDITOR_RETURN_TTL_MS}. The old pages never arm it — their scenarios
 * (old list → editor → save → old view, `/orders/:id` opened directly) stay as they were.
 */
@Injectable({ providedIn: 'root' })
export class OldEditorReturnService {
  private place?: OldEditorReturnPlace;
  private armedAt = 0;
  private saved?: () => void;

  constructor(
    private router: Router,
    private location: Location,
  ) {
    // Leaving the editor by any road but its «Сохранить» (the guard's redirect lands on a list or a card too) ends the operator's stay in it.
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(e => {
      if (!isEditorUrl(e.urlAfterRedirects)) {
        this.disarm();
      }
    });
  }

  /**
   * Leaves for the old editor — for a new order (no query), «Новый заказ на этот номер» (`phone`) or the order `orderId` — and remembers
   * where the operator leaves from: the list «Заказы» (its query params) or the full card. Anywhere else nothing is remembered and the
   * editor ends in the old view as it always did. The editor's own `backLink` (its «Отмена») is the page the operator is on.
   * `options` is for the pages of the new editor (`/order/new` sends the operator here with her booking): they say themselves where the save
   * brings her back to and where «Отмена» leads (see {@link OldEditorOpenOptions}); without them nothing changes.
   */
  openEditor(query: OldEditorQuery = {}, options: OldEditorOpenOptions = {}): Promise<boolean> {
    const url = this.location.path(true);
    this.place = options.place ?? this.placeOf(url);
    this.armedAt = Date.now();
    // the word of the page that sends her now: an earlier page's is no longer anybody's
    this.saved = options.saved;
    const queryParams: Params = {};
    if (query.orderId !== undefined) {
      queryParams['orderId'] = query.orderId;
    }
    if (query.phone !== undefined) {
      queryParams['phone'] = query.phone;
    }
    const extras: NavigationExtras = { state: { backLink: options.backLink ?? url }, ...(Object.keys(queryParams).length ? { queryParams } : {}) };
    return this.router.navigate([OLD_EDITOR_PATH], extras).then(
      reached => {
        if (!reached) {
          this.disarm();
        }
        return reached;
      },
      (err: unknown) => {
        this.disarm();
        throw err;
      },
    );
  }

  /**
   * The guard's question: the operator is on her way from the editor to the old view of order `idParam` — where is she to be taken instead?
   * `undefined`: nowhere, the old view is hers (not armed, expired, or she does not come from the editor). The id is the ROUTE's, not a
   * remembered one: a transferred order is another order, a new one had no id when the operator left.
   *
   * It does not disarm: the redirect's own `NavigationEnd` does, so a second «exit» fired while the first is still on its way (a save and
   * `orderPaid` together) is taken to the same place.
   */
  redirectFor(idParam: string | null): UrlTree | RedirectCommand | undefined {
    const place = this.armedPlace();
    if (!place || !idParam || !/^\d+$/.test(idParam) || !isEditorUrl(this.router.url)) {
      return undefined;
    }
    const id = Number(idParam);
    // the order is saved: the page that sent her is told, once (a save and `orderPaid` together ask the guard twice)
    this.tellSaved();
    if (place.kind === 'list') {
      return this.router.createUrlTree(['/orders'], { queryParams: { ...place.params, order_id: id } });
    }
    const card = this.router.createUrlTree(orderCardRoute(id));
    // the card the operator left had a way back (its «Заказы» / «Архив заказов» crumb): a redirect can carry the state, so the new card has it too
    return place.backState ? new RedirectCommand(card, { state: place.backState }) : card;
  }

  disarm(): void {
    this.place = undefined;
    this.saved = undefined;
  }

  private tellSaved(): void {
    const saved = this.saved;
    this.saved = undefined;
    try {
      saved?.();
    } catch {
      // what the page clears for itself must never stop the operator from reaching her order
    }
  }

  private armedPlace(): OldEditorReturnPlace | undefined {
    if (this.place && Date.now() - this.armedAt >= OLD_EDITOR_RETURN_TTL_MS) {
      this.disarm();
    }
    return this.place;
  }

  private placeOf(url: string): OldEditorReturnPlace | undefined {
    const tree = this.router.parseUrl(url);
    const segments = (tree.root.children[PRIMARY_OUTLET]?.segments ?? []).map(s => s.path);
    if (segments.length === 1 && segments[0] === 'orders') {
      return { kind: 'list', params: { ...tree.queryParams } };
    }
    if (segments.length === 2 && segments[0] === 'order') {
      return { kind: 'card', backState: this.cardBackState() };
    }
    return undefined;
  }

  private cardBackState(): Record<string, unknown> | undefined {
    const state: unknown = this.location.getState();
    if (!state || typeof state !== 'object') {
      return undefined;
    }
    const kept: Record<string, unknown> = {};
    for (const key of CARD_BACK_STATE_KEYS) {
      const value = (state as Record<string, unknown>)[key];
      if (value !== undefined) {
        kept[key] = value;
      }
    }
    return Object.keys(kept).length ? kept : undefined;
  }
}
